/**
 * Camera QR scanner with an explicit start/stop button. The camera is never opened
 * without a user action, and every use has a typed alternative next to it (WCAG 2.5.x,
 * Req 28.3). The same text seen again within {@link REPEAT_MS} is ignored, so one QR held
 * in front of the camera counts once. A camera failure is shown as a calm inline notice
 * with a Retry button, not raw body text.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../hooks';
import { CameraError, type CameraErrorCode, type StopScanning } from '../scanner/camera';
import { useServices } from '../services';

const REPEAT_MS = 2500;

type ScannerState = { phase: 'idle' } | { phase: 'starting' } | { phase: 'scanning' } | { phase: 'error'; code: CameraErrorCode };

export interface QrScannerProps {
  /** Called once per distinct decoded text. */
  onScan: (text: string) => void;
  /** Keep scanning after a result (product codes); default stops after the first. */
  continuous?: boolean;
  /** Accessible name of the start button, e.g. "Scan member QR". */
  startLabel?: string;
  /** Render the start button as the large full-width primary action. */
  primary?: boolean;
  disabled?: boolean;
}

export function QrScanner({ onScan, continuous = false, startLabel, primary = false, disabled = false }: QrScannerProps) {
  const t = useT();
  const { cameraScanner } = useServices();
  const videoRef = useRef<HTMLVideoElement>(null);
  const stopRef = useRef<StopScanning | undefined>(undefined);
  const lastRef = useRef<{ text: string; at: number } | undefined>(undefined);
  const onScanRef = useRef(onScan);
  const [state, setState] = useState<ScannerState>({ phase: 'idle' });
  // Cancels a start that resolves after stop/unmount.
  const generation = useRef(0);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  const stop = useCallback(() => {
    generation.current++;
    stopRef.current?.();
    stopRef.current = undefined;
    setState({ phase: 'idle' });
  }, []);

  useEffect(
    () => () => {
      generation.current++;
      stopRef.current?.();
    },
    [],
  );

  useEffect(() => {
    if (disabled && stopRef.current) stop();
  }, [disabled, stop]);

  const start = async () => {
    const video = videoRef.current;
    if (!video) return;
    const gen = ++generation.current;
    setState({ phase: 'starting' });
    try {
      const stopFn = await cameraScanner.start(video, (text) => {
        if (gen !== generation.current) return;
        const now = Date.now();
        const last = lastRef.current;
        if (last && last.text === text && now - last.at < REPEAT_MS) return;
        lastRef.current = { text, at: now };
        if (!continuous) stop();
        onScanRef.current(text);
      });
      if (gen !== generation.current) {
        stopFn();
        return;
      }
      stopRef.current = stopFn;
      setState({ phase: 'scanning' });
    } catch (cause) {
      if (gen !== generation.current) return;
      setState({ phase: 'error', code: cause instanceof CameraError ? cause.code : 'failed' });
    }
  };

  const active = state.phase === 'starting' || state.phase === 'scanning';
  const startClass = primary ? 'button button--primary button--block button--large' : 'button';
  return (
    <div className="scanner">
      {active ? (
        <button type="button" className="button" onClick={stop}>
          {t('operator.scanner.stop')}
        </button>
      ) : (
        <button type="button" className={startClass} onClick={() => void start()} disabled={disabled} data-testid="scanner-start">
          {startLabel ?? t('operator.scanner.start')}
        </button>
      )}
      <video
        ref={videoRef}
        className={active ? 'scanner__video' : 'scanner__video scanner__video--hidden'}
        aria-label={t('operator.scanner.videoLabel')}
        muted
        playsInline
      />
      {/* Starting/scanning progress is a polite status line. */}
      <p role="status" aria-live="polite" className={state.phase === 'starting' || state.phase === 'scanning' ? 'scanner__status' : 'visually-hidden'}>
        {state.phase === 'starting' ? t('operator.scanner.starting') : state.phase === 'scanning' ? t('operator.scanner.scanning') : ''}
      </p>
      {/* A camera failure is a calm inline notice (assertive) with a Retry button. */}
      {state.phase === 'error' && (
        <div className="camera-error" role="alert" data-testid="camera-error">
          <p className="camera-error__text">{t(`operator.scanner.errors.${state.code}`)}</p>
          <button type="button" className="button" onClick={() => void start()} disabled={disabled}>
            {t('operator.scanner.retry')}
          </button>
        </div>
      )}
    </div>
  );
}
